import React, { useState } from 'react';
import { NavLink, useLocation, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { makeStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import {
  Description as DescriptionIcon,
  AccountCircle as AccountCircleIcon,
  PlayCircleFilled as PlayCircleFilledIcon,
  Help as HelpIcon,
  KeyboardArrowLeft as ArrowLeft,
  KeyboardArrowRight as ArrowRight,
  ExitToApp as Logout,
} from '@mui/icons-material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import SmsIcon from '@mui/icons-material/Sms';
import Typography from '@mui/material/Typography';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StorageIcon from '@mui/icons-material/Storage';

import { resetProviderSearch } from '@apps/shared/src/providerSearch/actions';
import { resetAddCase } from '@apps/shared/src/addCase/actions';
import { resetMemberInfo } from '@apps/shared/src/getMember/actions';

import { roles as mediviRoles } from './globals';
import { RootState } from '../store';
import ModalLayout from './ModalLayout';

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    height: 'calc(100vh - 40px - 2.5rem)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'start',
    padding: '1rem 1rem 4rem 1rem',
  },
  navMenus: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',
    overflowY: 'scroll',
    '&::-webkit-scrollbar': {
      width: '1px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '1px',
    },
  },
  navMenu: {
    width: '95%',
  },
  navText: {
    textWrap: 'nowrap',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: 'rgba(0, 0, 0, 0.87)',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    letterSpacing: '-0.2px',
    textTransform: 'none',
  },
  shrinkedNavButton: {
    justifyContent: 'center !important',
    padding: '0.5rem 0.5rem 0.5rem 0.2rem !important',
  },
  navButton: {
    width: '100%',
    justifyContent: 'start',
    padding: '.5rem',
    minWidth: 0,
    textTransform: 'none',
    '& .MuiTypography-root': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  buttonContent: {
    display: 'flex',
    gap: '1rem',
    color: 'rgba(0, 0, 0, 0.87)',
    '& .MuiSvgIcon-root': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  active: {
    width: '98%',
    backgroundColor: '#EF7125',
    color: '#ffffff',
    borderRadius: '.2rem',
    '&.MuiButton-root:hover': {
      backgroundColor: '#EF7125',
    },
    '&.MuiButton-root': {
      color: '#ffffff',
    },
  },
  activeButtonContent: {
    color: '#ffffff',
    '& .MuiSvgIcon-root': {
      color: '#ffffff',
    },
  },
  activeNavText: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    color: '#ffffff',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    letterSpacing: '-0.2px',
  },
  btn: {
    marginTop: 'auto',
    width: '95%',
    whiteSpace: 'nowrap',
    backgroundColor: '#EF7125',
    color: '#FFFFFF',
    padding: '.6rem',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    letterSpacing: '0em',
    textAlign: 'center',
    '&.MuiButton-root': {
      minWidth: '0px',
    },
    '&.MuiButton-root:hover': {
      backgroundColor: 'chocolate',
    },
  },
  drawerIcon: {
    background: 'white',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    position: 'absolute',
    top: '15px',
    right: '-15px',
    display: 'flex',
    justifyContent: 'center',
    zIndex: 9,
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '24px',
    boxShadow: '0px 4px 4px 0px #0000001A',
  },
  subNavContainer: {
    paddingLeft: '2rem',
    paddingTop: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  subNavLink: {
    textDecoration: 'none',
    margin: '0.5rem 0',
    color: 'rgba(0, 0, 0, 0.87)',
    '&:hover': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  subNavText: {
    fontSize: '0.875rem',
  },
  activeSubNavText: {
    color: '#EF7125',
    fontWeight: 600,
  },
}));

const mapStateToProps = ({ user }: RootState) => ({
  currentUser: user.currentUser,
  isCaseMember: user.isCaseMember,
  hasClientViewRole: user.currentUser?.roles?.includes('medivi-caseviewclient') ?? false,
});

type SideNavBarProps = {
  updateParentState: (newState: boolean) => void;
};

// MODAL styling starts from here
function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}
// Modal ends here

const mapDispatchToProps = {
  resetProviderSearch,
  resetAddCase,
  resetMemberInfo,
};

const SideNavBar = ({
  currentUser,
  resetProviderSearch,
  resetAddCase,
  resetMemberInfo,
  isCaseMember,
  hasClientViewRole,
  updateParentState,
}: any & SideNavBarProps) => {
  const classes = useStyles();
  const history = useHistory();

  const roles = (currentUser && currentUser.roles) || [];

  const hasAdminRole = roles.indexOf(mediviRoles.admin) > -1;
  const hasAnalysisRole = roles.indexOf(mediviRoles.analysis) > -1;
  // const hasResearchRole = roles.indexOf(mediviRoles.code) > -1;
  const hasReadRole = roles.indexOf(mediviRoles.read) > -1;
  const hasBenchmarkRole = roles.indexOf(mediviRoles.benchmarks) > -1;
  const hasMobileReadRole = roles.indexOf(mediviRoles.mobileRead) > -1;
  const hasReportRole = roles.indexOf(mediviRoles.reports) > -1;
  const hasPlanHealthRoleReportRole = roles.indexOf(mediviRoles.planHealthReport) > -1;
  const hasMonthlyReportRole = roles.indexOf(mediviRoles.monthlyReport) > -1;
  const hasProviderAcceptanceReportRole = roles.indexOf(mediviRoles.providerAcceptance) > -1;
  const hasInviteRole = roles.indexOf(mediviRoles.invite) > -1;
  // const hasMediviTesterRole = roles.indexOf(mediviRoles.tester) > -1;
  const hasUpdateProviderExperience = roles.indexOf(mediviRoles.updateProviderExperience) > -1;
  const hasUpdateProviderExperienceReadOnly =
    roles.indexOf(mediviRoles.updateProviderExperienceReadOnly) > -1;

  const [showSideNav, setShowSideNav] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogout = () => {
    history.push('/logout');
    resetProviderSearch();
    resetAddCase();
    resetMemberInfo();
    setShowLogoutModal(false);
  };

  const handleOpenLogoutModal = () => {
    setShowLogoutModal(true);
  };

  const handleDrawer = () => {
    setShowSideNav(!showSideNav);
    updateParentState(!showSideNav);
  };

  const handleClose = () => {
    setShowLogoutModal(false);
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.navMenus}>
          {hasClientViewRole && (hasMobileReadRole || hasReadRole) && (
            <NavItem
              navigateTo="/my-cases"
              navText={showSideNav ? 'Cases' : ''}
              Icon={DescriptionIcon}
              showSideNav={showSideNav}
            />
          )}
          {!hasClientViewRole &&
            isCaseMember &&
            (hasMobileReadRole || hasReadRole || hasMobileReadRole) && (
              <NavItem
                navigateTo="/my-cases"
                navText={showSideNav ? `My Cases` : ''}
                Icon={DescriptionIcon}
                showSideNav={showSideNav}
              />
            )}
          {(hasMobileReadRole || hasReadRole || hasMobileReadRole) && (
            <NavItem
              navigateTo="/provider-search"
              navText={showSideNav ? 'Provider Search' : ''}
              Icon={PersonIcon}
              showSideNav={showSideNav}
            />
          )}
          {(hasUpdateProviderExperience || hasUpdateProviderExperienceReadOnly) && (
            <NavItem
              navigateTo="/provider-search-update"
              navText={showSideNav ? 'Update Provider Search' : ''}
              Icon={StorageIcon}
              showSideNav={showSideNav}
            />
          )}
          {(hasMobileReadRole || hasReadRole || hasMobileReadRole) && (
            <NavItem
              navigateTo="/instructional-video"
              navText={showSideNav ? 'Instructional Video' : ''}
              Icon={PlayCircleFilledIcon}
              showSideNav={showSideNav}
            />
          )}
          {hasBenchmarkRole && (
            <NavItem
              navigateTo="/benchmarks"
              navText={showSideNav ? 'Benchmarks' : ''}
              Icon={TrendingUpIcon}
              showSideNav={showSideNav}
            />
          )}
          {(hasAdminRole || hasInviteRole) && (
            <NavItem
              navigateTo="/users"
              navText={showSideNav ? 'Users' : ''}
              Icon={SupervisorAccountIcon}
              showSideNav={showSideNav}
            />
          )}
          {hasAnalysisRole && (
            <NavItem
              navigateTo="/analysis"
              navText={showSideNav ? 'Market Analysis' : ''}
              Icon={GraphicEqIcon}
              showSideNav={showSideNav}
            />
          )}
          {hasReportRole &&
            (hasMonthlyReportRole ||
              hasPlanHealthRoleReportRole ||
              hasProviderAcceptanceReportRole) && (
              <NavItem
                navigateTo="/reports/monthly-reporting"
                navText={showSideNav ? 'Reports' : ''}
                Icon={AssessmentIcon}
                showSideNav={showSideNav}
                subNavItems={
                  [
                    hasMonthlyReportRole && {
                      label: 'Monthly Reporting',
                      path: '/reports/monthly-reporting',
                    },
                    hasProviderAcceptanceReportRole && {
                      label: 'Provider Acceptance',
                      path: '/reports/providerAcceptance-reporting',
                    },
                    hasPlanHealthRoleReportRole && {
                      label: 'Plan Health Review',
                      path: '/reports/planHealth-reporting',
                    },
                  ].filter(Boolean) as SubNavItem[]
                } // Filters out false/null items
              />
            )}
          {(hasMobileReadRole || hasReadRole || hasMobileReadRole) && (
            <NavItem
              navigateTo="/contact-us"
              navText={showSideNav ? 'Contact Us' : ''}
              Icon={SmsIcon}
              showSideNav={showSideNav}
            />
          )}
          {(hasMobileReadRole || hasReadRole || hasMobileReadRole) && (
            <NavItem
              navigateTo="/FAQ"
              navText={showSideNav ? 'FAQs' : ''}
              Icon={HelpIcon}
              showSideNav={showSideNav}
            />
          )}
          {(hasMobileReadRole || hasReadRole || hasMobileReadRole) && (
            <NavItem
              navigateTo="/privacy-policy"
              navText={showSideNav ? 'Privacy Policy' : ''}
              Icon={InsertDriveFileIcon}
              showSideNav={showSideNav}
            />
          )}
          {(hasMobileReadRole || hasReadRole || hasMobileReadRole) && (
            <NavItem
              navigateTo="/my-account"
              navText={showSideNav ? 'Account Settings' : ''}
              Icon={AccountCircleIcon}
              showSideNav={showSideNav}
            />
          )}
        </div>
        <Button className={classes.btn} onClick={handleOpenLogoutModal} role="button">
          {showSideNav ? 'LOG OUT' : <Logout />}
        </Button>
        <div
          className={classes.drawerIcon}
          onClick={handleDrawer}
          role="button"
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleDrawer();
            }
          }}
          tabIndex={0}
        >
          {showSideNav ? <ArrowLeft /> : <ArrowRight />}
        </div>

        <div>
          <ModalLayout
            showModal={showLogoutModal}
            title="Are You Sure?"
            message={
              <>
                <div>Do you really want to log out from your account?</div>
              </>
            }
            handleModalClose={handleClose}
            handleSuccess={handleLogout}
            closeTitle="CANCEL"
            actionTitle="LOG OUT"
          />
        </div>
      </div>
    </>
  );
};

interface NavItemProps {
  navigateTo: string;
  navText: string;
  showSideNav: boolean;
  Icon: React.ElementType;
  subNavItems?: SubNavItem[];
}
interface SubNavItem {
  label: string;
  path: string;
}

const NavItem = ({ navigateTo, navText, Icon, showSideNav, subNavItems = [] }: NavItemProps) => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useHistory();

  // Active check logic
  let isActive = location.pathname === navigateTo;

  if (navigateTo === '/my-cases') {
    isActive = [
      '/my-cases',
      '/member-detail',
      '/provider-detail',
      '/upload-file',
      '/notifications',
    ].includes(location.pathname);
  } else if (navigateTo === '/my-account') {
    isActive = ['/my-account', '/dependent-information', '/add-plan'].includes(location.pathname);
  }

  const isSubItemActive = subNavItems.some(item => location.pathname === item.path);
  const shouldExpand = isActive || isSubItemActive;
  const isActiveOrSubActive = isActive || isSubItemActive;

  // Handle click
  const handleClick = () => {
    if (subNavItems.length > 0) {
      navigate.push(subNavItems[0].path); // Navigate to first subnav
    } else {
      navigate.push(navigateTo); // Default nav
    }
  };

  return (
    <div className={classes.navMenu}>
      <div className={`${classes.navMenu} ${isActiveOrSubActive ? classes.active : ''}`}>
        <Button
          className={`${classes.navButton} ${showSideNav ? '' : classes.shrinkedNavButton}`}
          onClick={handleClick}
        >
          <div
            className={`${classes.buttonContent} ${
              isActiveOrSubActive ? classes.activeButtonContent : ''
            }`}
          >
            <Icon />
            <Typography
              className={`${classes.navText} ${isActiveOrSubActive ? classes.activeNavText : ''}`}
            >
              {navText}
            </Typography>
          </div>
        </Button>
      </div>

      {showSideNav && shouldExpand && subNavItems.length > 0 && (
        <div className={classes.subNavContainer}>
          {subNavItems.map(item => (
            <NavLink key={item.path} to={item.path} className={classes.subNavLink}>
              <Typography
                className={`${classes.subNavText} ${
                  location.pathname === item.path ? classes.activeSubNavText : ''
                }`}
              >
                {item.label}
              </Typography>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(SideNavBar);
